import { Text, type MantineFontSize } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { currencyFormat } from "../../../utils";
import classes from "../list.module.css";

type Props = {
  amount?: number | null;
  strikeThrough?: boolean;
  dimmed?: boolean;
  size?: MantineFontSize;
  fw?: number;
  ta?: "left" | "right" | "center";
  /** Table amounts stack without ₮ (default). Cards may pass true. */
  withSymbol?: boolean;
  /** Emphasize remaining balance */
  emphasis?: boolean;
};

/** Table/list money — right-aligned tabular nums, nowrap. */
export function MoneyCell({
  amount,
  strikeThrough,
  dimmed,
  size = "sm",
  fw,
  ta = "right",
  withSymbol = false,
  emphasis,
}: Props) {
  const { t } = useTranslation("common");
  const dash = t("list.dash");
  const weight = fw ?? (emphasis ? 650 : undefined);
  const color = dimmed ? "gray.7" : emphasis || withSymbol ? undefined : "gray.7";
  const className = strikeThrough ? `${classes.moneyCell} ${classes.moneyCellStrike}` : classes.moneyCell;

  if (amount == null || amount === 0) {
    return (
      <Text ta={ta} size={size} c="dimmed" fw={weight} className={classes.moneyCell}>
        {dash}
      </Text>
    );
  }

  const num = Number(amount);
  if (!Number.isFinite(num) || num === 0) {
    return (
      <Text ta={ta} size={size} c="dimmed" fw={weight} className={classes.moneyCell}>
        {dash}
      </Text>
    );
  }

  return (
    <Text
      ta={ta}
      size={size}
      fw={weight}
      c={color}
      className={`${className}${emphasis ? ` ${classes.moneyCellEmphasis}` : ""}`}
    >
      {currencyFormat(num, { withSymbol })}
    </Text>
  );
}
